/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@emotion/react'
import { ColumnChart } from 'components/ColumnChart'
import { Loader } from 'components/Loader'
import * as commonClasses from '../styles'
import * as generalAnalyticsClasses from '../AttemptsAnalyticsChart/styles'
import { TooltipContent } from '../TooltipContent'
import * as classes from './styles'
import { ChartDataVariables, useChartData } from './hooks'

type ChartData = {
  xAxisValue: number
  yAxisValue: number
  correct: number
  incorrect: number
}

const CHART_OPTIONS = {
  scales: {
    y: {
      suggestedMax: 5,
      ticks: {
        callback: (value: unknown) => Math.round(Number(value)),
      },
    },
    x: {
      ticks: {
        callback: (value: unknown) => `Level ${value}`,
      },
    },
  },
}

type Props = {
  chartDataVariables: ChartDataVariables
}

export const SpecificGameAnalytics: FC<Props> = ({ chartDataVariables }) => {
  const theme = useTheme()

  const { isInitiallyLoaded, chartData, loading } = useChartData(chartDataVariables)

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        backgroundColor: '#f3f7fb',
        p: '8px 9px',
        borderRadius: '6px',
        borderColor: theme.backgrounds.backgroundOne,
        boxShadow: `0px -1px 0px 0px rgba(255, 255, 255, 0.04), 0px 0px 0px 1px rgba(255, 255, 255, 0.12), 0px 2px 4px 0px rgba(0, 0, 0, 0.32), 0px 4px 8px 0px rgba(0, 0, 0, 0.32)`,
      },
    },
  }

  const renderColumn = (value: ChartData, percentage: number, index: number) => {
    const correctPercentage = (value.correct / (value.correct + value.incorrect)) * percentage

    const isEmpty = value.yAxisValue === 0

    return (
      <div css={classes.column}>
        {isEmpty ? (
          <div css={classes.emptyItem} />
        ) : (
          <>
            <div css={classes.item(percentage, false)} />
            <div css={[classes.item(correctPercentage, true), classes.growAnimation(index)]} />
            <Tooltip
              title={
                <TooltipContent
                  title={`Level ${value.xAxisValue}`}
                  items={[
                    { title: 'Correct', value: value.correct.toString() },
                    { title: 'Incorrect', value: value.incorrect.toString() },
                  ]}
                />
              }
              slotProps={tooltipSlotProps}
              placement="top"
            >
              <div css={classes.dummyItem(percentage)} />
            </Tooltip>
          </>
        )}
      </div>
    )
  }

  const renderLegend = () => (
    <Typography variant="h5" css={classes.legend}>
      <div css={classes.legendItem}>
        <div css={classes.dot(true)} /> Correct
      </div>
      <div css={classes.legendItem}>
        <div css={classes.dot(false)} /> Incorrect
      </div>
    </Typography>
  )

  return (
    <>
      <div css={[generalAnalyticsClasses.headingWrapper, classes.headingWrapper]}>
        <Typography variant="h5" css={commonClasses.title}>
          Specific-Game Analytics
        </Typography>
      </div>

      <div css={commonClasses.chartCard}>
        <div css={commonClasses.cardHeading}>
          <div css={commonClasses.headingTitles}>
            <Typography variant="h5" css={commonClasses.cardTitle}>
              Level Performance
            </Typography>

            {renderLegend()}
          </div>
        </div>

        <div css={classes.chartWrapper}>
          {(loading || !isInitiallyLoaded) && (
            <div css={generalAnalyticsClasses.chartDataLoaderWrapper}>
              <Loader />
            </div>
          )}

          <ColumnChart css={classes.chart} options={CHART_OPTIONS} values={chartData} renderColumn={renderColumn} />
        </div>
      </div>
    </>
  )
}
